import {
  FocusEventHandler,
  Fragment,
  ReactElement,
  ReactNode,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import RSelect, {
  InputProps as RSelectInputProps,
  Styles,
  components,
  MenuListComponentProps,
  createFilter,
} from 'react-select';
import type { Option as SelectOptionType } from 'react-select/src/filters';

import styled from 'styled-components';

import { calculateTextWidth, useAriaId } from '@components/component-helpers';
import { FixedFullscreenOverlay } from '@components/fixed-fullscreen-overlay';
import { FormError } from '@components/form-error';
import { FormLabel } from '@components/form-label';
import { IconProps } from '@components/icons';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconDropdown } from '@components/icons/dropdown';
import { IconFormIndicatorLocked } from '@components/icons/form-indicator-locked';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { Tooltip } from '@components/tooltip';
import { VisuallyHidden } from '@components/visually-hidden';
import { useEvent } from '@hooks/use-event';
import { useIsOverflowing } from '@hooks/use-is-overflowing';
import { menuOpenStore } from '@stores/menu-open-store';

import { useSelectTranslations } from './locale';
import {
  HelpText,
  InputBox,
  MenuListPositionWrapper,
  OptionContainer,
  Prefix,
  PrefixContainer,
  styles,
} from './styles';

export { createFilter } from 'react-select';

const ADD_OPTION_VALUE = '__add_option__';

export type SelectOption<T = string> = {
  label: string | ReactNode;
  filterText?: string;
  value: T;
  helpText?: string;
  isDisabled?: boolean;
  tooltip?: string;
  singleValue?: JSX.Element;
};

export type SelectProps = {
  options: SelectOption[];
  defaultValue?: string | SelectOption | null;
  value?: string | SelectOption | null;
  onChange?: (value: string) => void;
  onSelectionChange?: (option: SelectOption | null) => void;
  onBlur?: FocusEventHandler;
  placeholder?: string;
  noOptions?: (obj: { inputValue: string }) => string | null;
  label?: string;
  labelIcon?: ReactElement<IconProps>;
  isDisabled?: boolean;
  isSearchable?: boolean;
  isClearable?: boolean;
  prefix?: string;
  parseSingleValue?: (option: SelectOption) => React.ReactNode;
  error?: string;
  helpText?: string;
  menuPortalTarget?: HTMLElement;
  filterOption?(option: SelectOption, input: string): boolean;
  hasFilterText?: boolean;
  id?: string;
  overflowWidth?: boolean;
  styles?: Styles<any, boolean>;
  hideLabel?: boolean;
  optionContentFullWidth?: boolean;
  addOption?: {
    label: string;
    href: string;
  };
};

const ClearIndicator = (props: any) => {
  const handleClearValue = (clearValue: any) => () => {
    clearValue();
  };
  const { clearValue, ...innerProps } = props;
  return (
    components.ClearIndicator && (
      <components.ClearIndicator {...innerProps}>
        <IconCloseRegular
          onMouseDown={(e: any) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onClick={handleClearValue(clearValue)}
        />
      </components.ClearIndicator>
    )
  );
};

const DropdownIndicator = (props: any) => {
  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        {props.isDisabled ? <IconFormIndicatorLocked /> : <IconDropdown />}
      </components.DropdownIndicator>
    )
  );
};

const Option = (props: any) => {
  const textRef = useRef<HTMLDivElement>(null);
  const { tooltip, helpText, isAddOption, href } = props.data;
  const isTruncated = useIsOverflowing(textRef);

  if (isAddOption) {
    return (
      <components.Option
        {...props}
        innerProps={{
          ...props.innerProps,
          onClick: (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            window.open(href, '_blank', 'noopener,noreferrer');
          },
        }}
      >
        <div>
          <Stack orientation="horizontal" align="center" space={1}>
            <IconAddPlus size={24} color="elementPurple80" />
            <Text font="regular14" color="textPrimary" text={props.data.label} />
            <IconOpenNewTab size={16} color="elementPurple50" />
          </Stack>
        </div>
      </components.Option>
    );
  }

  const content = (
    <OptionContainer $helpText={helpText}>
      <TruncatedText ref={textRef}>{props.children}</TruncatedText>
      {helpText && <HelpText>{helpText}</HelpText>}
    </OptionContainer>
  );

  const children = isTruncated ? (
    <Tooltip placement="left" content={tooltip ?? props.data.label}>
      {content}
    </Tooltip>
  ) : (
    content
  );

  return (
    <components.Option
      {...props}
      innerProps={{
        ...props.innerProps,
        onClick: (e) => {
          e.stopPropagation();
          props.innerProps.onClick(e);
        },
      }}
    >
      {children}
    </components.Option>
  );
};

const TruncatedText = styled.div({
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
});

const SingleValue = (props: any) => {
  const textRef = useRef<HTMLDivElement>(null);
  const isTruncated = useIsOverflowing(textRef);

  const content = props.selectProps.prefix ? (
    <PrefixContainer>
      <Prefix>{props.selectProps.prefix}</Prefix>
      <TruncatedText ref={textRef}>{props.children}</TruncatedText>
    </PrefixContainer>
  ) : props.selectProps.parseSingleValue ? (
    <TruncatedText ref={textRef}>{props.selectProps.parseSingleValue(props.data)}</TruncatedText>
  ) : (
    <TruncatedText ref={textRef}>{props.children}</TruncatedText>
  );

  const children =
    isTruncated && props.data?.tooltip ? (
      <Tooltip placement="top" content={props.data.tooltip}>
        {content}
      </Tooltip>
    ) : (
      content
    );

  return <components.SingleValue {...props}>{children}</components.SingleValue>;
};

export function Select(props: SelectProps) {
  const { t } = useSelectTranslations();
  const {
    options,
    value = undefined,
    defaultValue = undefined,
    onChange,
    onSelectionChange,
    onBlur,
    placeholder = t('placeholder'),
    noOptions = () => t('no_options'),
    label,
    labelIcon,
    isDisabled = false,
    isSearchable = true,
    isClearable = true,
    prefix,
    parseSingleValue,
    error = '',
    helpText = '',
    menuPortalTarget,
    id: providedId,
    styles: additionalStyles,
    hideLabel,
    hasFilterText,
    filterOption,
    addOption,
    ...rest
  } = props;

  const selectId = useAriaId('select');
  const ref = useRef<HTMLDivElement | null>(null);

  // Transform options to include addOption as a special option at the end
  const transformedOptions = useMemo(() => {
    if (!addOption) return options;
    return [
      ...options,
      {
        label: addOption.label,
        value: ADD_OPTION_VALUE,
        isAddOption: true,
        href: addOption.href,
      },
    ];
  }, [options, addOption]);

  const isControlled = value !== undefined;

  const [localSelected, setLocalSelected] = useState<SelectOption | null>(null);

  const handleChange = useEvent((selected: SelectOption | undefined) => {
    // Prevent selecting the "add option" - it opens a link instead
    if (selected && (selected as any).isAddOption) {
      return;
    }
    setLocalSelected(selected ?? null);
    onChange?.(selected ? selected.value : '');
    onSelectionChange?.(selected ?? null);
  });

  const handleMenuOpen = useEvent(() => {
    menuOpenStore.increment();
  });

  const handleMenuClose = useEvent(() => {
    menuOpenStore.decrement();
  });

  const selectedValue = isControlled
    ? typeof value === 'object'
      ? value
      : options.find((o) => o.value === value)
    : localSelected;

  const syncLocalValue = useEvent(() => {
    // If it's a controlled input, we don't care.
    if (value) return;

    // We already selected a value, make sure the new choices contain that value.
    if (localSelected) {
      if (options.find((opt) => opt.value === localSelected.value)) return;
    }

    if (defaultValue) {
      const defaultValueOption =
        typeof defaultValue === 'object'
          ? defaultValue
          : options.find((o) => o.value === defaultValue);

      if (localSelected && localSelected.value !== defaultValueOption?.value) {
        // Calling 'onChange' is fine since we're re-selecting defaultValue
        // after the user changed to a different value
        return handleChange(defaultValueOption);
      }

      return setLocalSelected(defaultValueOption ?? null);
    }

    if (localSelected) {
      // Previously selected item is not in the new choices
      // and neither is the defaultValue (if set), so we need to unset.
      return handleChange(undefined);
    }
  });

  useLayoutEffect(() => {
    syncLocalValue();
  }, [options, defaultValue, syncLocalValue]);

  const LabelWrapper = hideLabel ? VisuallyHidden : Fragment;

  return (
    <Stack space={1} ref={ref}>
      <LabelWrapper>
        <FormLabel htmlFor={selectId} text={label} icon={labelIcon} />
      </LabelWrapper>
      <RSelect
        id={providedId}
        instanceId={providedId}
        inputId={selectId}
        value={selectedValue}
        onChange={handleChange}
        isDisabled={isDisabled}
        onBlur={onBlur}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        menuPosition="fixed"
        minMenuHeight={200}
        menuPlacement="auto"
        menuPortalTarget={props.menuPortalTarget ?? document.body}
        noOptionsMessage={noOptions}
        closeMenuOnSelect={true}
        styles={{ ...styles, ...additionalStyles }}
        isClearable={isClearable}
        isSearchable={isSearchable}
        placeholder={placeholder}
        options={transformedOptions}
        prefix={prefix}
        parseSingleValue={parseSingleValue}
        tabSelectsValue={false}
        error={error}
        filterOption={hasFilterText ? filterTextOptionFilter : filterOption}
        components={{
          MenuList,
          Option,
          SingleValue,
          Input: CustomInput,
          IndicatorSeparator: () => null,
          DropdownIndicator,
          ClearIndicator,
        }}
        {...rest}
      />

      {(error || helpText) && (
        <div>
          <FormError text={error} />
          {helpText && <Text font="regular14" color="purple80" text={helpText} />}
        </div>
      )}
    </Stack>
  );
}

const filterTextOptionFilter = createFilter({
  stringify: (option: SelectOptionType) => (option.data as SelectOption)?.filterText ?? '',
});

const MenuList = (props: MenuListComponentProps<any, boolean, any>) => {
  return (
    <>
      <FixedFullscreenOverlay onClick={props.selectProps.onMenuClose} />
      <MenuListPositionWrapper>
        <components.MenuList {...props} />
      </MenuListPositionWrapper>
    </>
  );
};

function CustomInput(props: RSelectInputProps) {
  let {
    innerRef,
    cx,
    getStyles,
    theme,
    isHidden,
    isDisabled,
    // @ts-ignore
    value,
    ...rest
  } = props;
  return (
    <InputBox
      // @ts-ignore stfu
      ref={innerRef}
      style={{
        width: calculateTextWidth(value, "normal 400 16px 'Work Sans'"),
      }}
      value={value}
      {...rest}
    />
  );
}
